import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, MapPin, FileText, Trash2, List, Info, Layers, BrainCircuit, RefreshCw, ListFilter, ScanSearch, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { GeoObject, ObjectType, Organization, Geometry, File as ApiFile } from "@/types/api-v2";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGuard } from "@/components/PermissionGuard";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuthV2";
import { objectsApi, statsApi } from "@/services/api-v2.service";
import { buildObjectTypesHierarchy, flattenObjectTypesHierarchy } from "@/utils/objectTypesHierarchy";
import { buildOrganizationsHierarchy, flattenOrganizationsHierarchy } from "@/utils/organizationsHierarchy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { MultiSelectCombobox, type MultiSelectOption } from "@/components/ui/multi-select-combobox";
import { useMapUi, type SidebarTab } from "@/contexts/MapUiContext";
import { useGeographyFilter } from "@/contexts/GeographyFilterContext";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { ModuleRegistryTitle } from "@/components/shared/ModuleRegistryTitle";
import ObjectPanel from "@/components/ObjectPanel";
import AIChatPanel from "@/components/AIChatPanel";
import LayerPanel from "@/components/workspace/LayerPanel";
import { cn } from "@/lib/utils";
import { EntityListItemShell } from "@/components/shared/EntityListItemShell";
import { EntityListPaneHeader } from "@/components/shared/EntityListPaneHeader";
import { EntitySearchInput } from "@/components/shared/EntitySearchInput";
import { EntityListEmptyState, EntityListLoadingState } from "@/components/shared/EntityListStates";

/** Было w-96 (384px); +25% ≈ 480px */
const SIDEBAR_WIDTH_DEFAULT = 480;
const SIDEBAR_WIDTH_MIN = 280;
const SIDEBAR_WIDTH_STORAGE_KEY = "geopulseml.sidebar.width";

/** Ограничение: не уже минимума (если экран позволяет) и не шире 50% окна. */
function clampSidebarWidthPx(w: number): number {
  if (typeof window === "undefined") return w;
  const maxAllowed = window.innerWidth * 0.5;
  const minAllowed = Math.min(SIDEBAR_WIDTH_MIN, maxAllowed);
  return Math.round(Math.max(minAllowed, Math.min(w, maxAllowed)));
}

interface SidebarProps {
  objects: GeoObject[];
  objectTypes: ObjectType[];
  organizations: Organization[];
  isLoading?: boolean;
  onCreateObject: () => void;
  selectedObjectGeometries?: Geometry[];
  selectedObjectFiles?: ApiFile[];
  geometriesLoading?: boolean;
  onCardDirtyChange?: (dirty: boolean) => void;
  /** Уход с вкладки «Детали» / смена вкладки сайдбара с учётом dirty. */
  requestSidebarTabChange?: (tab: SidebarTab) => void;
  /** Id объектов из выбора контуром; null — фильтр неактивен. */
  contourFilterObjectIds?: number[] | null;
  onClearContourFilter?: () => void;
}

export default function Sidebar({
  objects,
  objectTypes,
  organizations,
  isLoading = false,
  onCreateObject,
  selectedObjectGeometries = [],
  selectedObjectFiles = [],
  geometriesLoading = false,
  onCardDirtyChange,
  requestSidebarTabChange,
  contourFilterObjectIds = null,
  onClearContourFilter,
}: SidebarProps) {
  const { hasPermission } = useAuth();
  const {
    selectedObject,
    selectObject,
    clearSelectedObject,
    multiSelectMode: isSelectionMode,
    setMultiSelectMode,
    multiSelectedObjectIds,
    toggleMultiSelectedObject,
    setMultiSelectedObjectIds,
    clearMultiSelection,
    sidebarTab,
    setSidebarTab,
    focusedGeometryId,
    isSidebarCollapsed: isCollapsed,
    refreshMapVectorTiles,
  } = useMapUi();
  const { territories, combine, journalSync, setJournalSync } = useGeographyFilter();
  const selectedObjects = useMemo(
    () => new Set(multiSelectedObjectIds),
    [multiSelectedObjectIds],
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  // Множественный выбор для типов объектов и организаций
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [showActive, setShowActive] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const listFiltersActive =
    selectedTypes.length > 0 ||
    selectedOrgs.length > 0 ||
    !(showActive && !showArchived) ||
    journalSync;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchTerm]);
  const [objectGeometries, setObjectGeometries] = useState<Record<number, number>>({});
  const [objectFiles, setObjectFiles] = useState<Record<number, number>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showBulkFieldsDialog, setShowBulkFieldsDialog] = useState(false);
  const [bulkNewStatus, setBulkNewStatus] = useState<"active" | "archived">("archived");
  const [bulkTypeId, setBulkTypeId] = useState<string>("");
  const [bulkOrganizationId, setBulkOrganizationId] = useState<string>("");
  // Мутация для массового удаления объектов
  const deleteObjectsMutation = useMutation({
    mutationFn: async (objectIds: number[]) => {
      const promises = objectIds.map(id => objectsApi.delete(id));
      await Promise.all(promises);
    },
    onSuccess: (_data, objectIds) => {
      toast({
        title: "Успех",
        description: `Удалено объектов: ${objectIds.length}`,
      });
      clearMultiSelection();
      setMultiSelectMode(false);
      queryClient.invalidateQueries({ queryKey: ["objects"] });
      refreshMapVectorTiles();
    },
    onError: (error) => {
      toast({
        title: "Ошибка",
        description: `Не удалось удалить объекты: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: number[]; status: string }) => {
      const promises = ids.map(id => objectsApi.update(id, { status }));
      await Promise.all(promises);
    },
    onSuccess: (_data, vars) => {
      toast({ title: "Статус обновлён", description: `Обновлено объектов: ${vars.ids.length}` });
      clearMultiSelection();
      setMultiSelectMode(false);
      queryClient.invalidateQueries({ queryKey: ["objects"] });
    },
    onError: (error) => {
      toast({ title: "Ошибка", description: `Не удалось обновить статус: ${error.message}`, variant: "destructive" });
    },
  });

  const updateBulkFieldsMutation = useMutation({
    mutationFn: async ({ ids, klsId, organizationId }: { ids: number[]; klsId?: number; organizationId?: number }) => {
      const payload: Record<string, number> = {};
      if (klsId !== undefined) payload.kls_id = klsId;
      if (organizationId !== undefined) payload.organization_id = organizationId;
      const promises = ids.map((id) => objectsApi.update(id, payload));
      await Promise.all(promises);
    },
    onSuccess: (_data, vars) => {
      toast({ title: "Поля обновлены", description: `Обновлено объектов: ${vars.ids.length}` });
      clearMultiSelection();
      setMultiSelectMode(false);
      setBulkTypeId("");
      setBulkOrganizationId("");
      queryClient.invalidateQueries({ queryKey: ["objects"] });
    },
    onError: (error) => {
      toast({
        title: "Ошибка",
        description: `Не удалось обновить поля: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Функции для управления выбором объектов (shared с картой — Wave 1.3)
  const toggleObjectSelection = (objectId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    toggleMultiSelectedObject(objectId);
  };

  const selectAllObjects = () => {
    setMultiSelectedObjectIds(filteredObjects.map((obj) => obj.id));
    setMultiSelectMode(true);
  };

  const clearSelection = () => {
    clearMultiSelection();
  };

  const toggleSelectionMode = () => {
    if (isSelectionMode) {
      setMultiSelectMode(false);
      clearMultiSelection();
    } else {
      setMultiSelectMode(true);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedObjects.size > 0) {
      setShowDeleteDialog(true);
    }
  };

  const confirmDelete = () => {
    deleteObjectsMutation.mutate(Array.from(selectedObjects));
    setShowDeleteDialog(false);
  };
  
  // Строим иерархию типов объектов для отображения
  const objectTypesHierarchy = useMemo(() => {
    if (!objectTypes || objectTypes.length === 0) return [];
    
    const typesWithRubrika = objectTypes.filter(t => t.rubrika && t.rubrika.trim().length > 0);
    
    if (typesWithRubrika.length > 0) {
      const hierarchy = buildObjectTypesHierarchy(objectTypes);
      const flattened = flattenObjectTypesHierarchy(hierarchy);
      if (flattened.length > 0) return flattened;
    }
    
    const typesWithParent = objectTypes.filter(t => t.parent_id !== null && t.parent_id !== undefined);
    const hasParentId = typesWithParent.length > 0;
    
    if (hasParentId) {
      // Строим иерархию на основе parent_id
      const buildHierarchyFromParent = (
        nodes: ObjectType[],
        parentId: number | null | undefined = null,
        level: number = 0
      ): Array<ObjectType & { displayName: string; indentLevel: number }> => {
        // Для корневых узлов (parentId === null) ищем узлы без родителя
        const children = parentId === null 
          ? nodes.filter(t => t.parent_id === null || t.parent_id === undefined)
          : nodes.filter(t => t.parent_id === parentId);
        
        const result: Array<ObjectType & { displayName: string; indentLevel: number }> = [];
        
        children.forEach((type) => {
          result.push({
            ...type,
            displayName: type.name, // Имя без форматирования, отступы через CSS
            indentLevel: level,
          });
          
          // Рекурсивно добавляем дочерние элементы
          const childItems = buildHierarchyFromParent(nodes, type.id, level + 1);
          result.push(...childItems);
        });
        
        return result;
      };
      
      const hierarchyFromParent = buildHierarchyFromParent(objectTypes);
      if (hierarchyFromParent.length > 0) {
        return hierarchyFromParent;
      }
    }
    
    // Fallback: плоский список
    return objectTypes.map(t => ({
      ...t,
      displayName: t.name,
      indentLevel: 0,
    }));
  }, [objectTypes]);
  
  // Преобразование типов объектов в опции для MultiSelectCombobox
  const objectTypeOptions = useMemo((): MultiSelectOption[] => {
    if (objectTypesHierarchy.length > 0) {
      return objectTypesHierarchy.map((type) => ({
        value: type.id.toString(),
        label: type.name_short || type.name,
        shortLabel: type.name_short,
        fullLabel: type.name,
        indentLevel: type.indentLevel,
      }));
    }
    
    if (objectTypes && objectTypes.length > 0) {
      return objectTypes.map((type) => ({
        value: type.id.toString(),
        label: type.name_short || type.name,
        shortLabel: type.name_short,
        fullLabel: type.name,
        indentLevel: 0,
      }));
    }
    
    return [];
  }, [objectTypesHierarchy, objectTypes]);
  
  // Иерархия организаций (построенная на основе rubrika из KLS)
  const organizationsHierarchy = useMemo(() => {
    if (!organizations || organizations.length === 0) return [];
    const tree = buildOrganizationsHierarchy(organizations);
    return flattenOrganizationsHierarchy(tree);
  }, [organizations]);

  // Преобразование организаций в опции для MultiSelectCombobox
  const organizationOptions = useMemo((): MultiSelectOption[] => {
    if (organizationsHierarchy.length > 0) {
      return organizationsHierarchy.map((org) => ({
        value: org.id.toString(),
        label: org.name_short || org.name,
        shortLabel: org.name_short,
        fullLabel: org.name,
        indentLevel: org.indentLevel,
      }));
    }
    
    // Fallback: плоский список
    return organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name_short || org.name,
      shortLabel: org.name_short,
      fullLabel: org.name,
      indentLevel: 0,
    }));
  }, [organizationsHierarchy, organizations]);

  const bulkTypeOptions = useMemo((): ComboboxOption[] => {
    return objectTypeOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
      shortLabel: opt.shortLabel,
      fullLabel: opt.fullLabel,
      indentLevel: opt.indentLevel,
    }));
  }, [objectTypeOptions]);

  const bulkOrganizationOptions = useMemo((): ComboboxOption[] => {
    return organizationOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
      shortLabel: opt.shortLabel,
      fullLabel: opt.fullLabel,
      indentLevel: opt.indentLevel,
    }));
  }, [organizationOptions]);

  const contourIdSet = useMemo(() => {
    if (!contourFilterObjectIds) return null;
    return new Set(contourFilterObjectIds);
  }, [contourFilterObjectIds]);

  const filteredObjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (objects || []).filter((object) => {
      const matchesSearch =
        !term ||
        object.name?.toLowerCase().includes(term) ||
        object.full_name?.toLowerCase().includes(term) ||
        object.address?.toLowerCase().includes(term) ||
        object.description?.toLowerCase().includes(term);

      const matchesType =
        selectedTypes.length === 0 ||
        selectedTypes.includes(object.kls_id?.toString() || "");

      const matchesOrg =
        selectedOrgs.length === 0 ||
        selectedOrgs.includes(object.organization_id?.toString() || "");

      const statusValue = object.status || "active";
      const matchesStatus = (() => {
        if (!showActive && !showArchived) return true;
        if (showActive && showArchived) return true;
        if (showActive) return statusValue !== "archived";
        if (showArchived) return statusValue === "archived";
        return true;
      })();

      const matchesContour = !contourIdSet || contourIdSet.has(object.id);

      return matchesSearch && matchesType && matchesOrg && matchesStatus && matchesContour;
    });
  }, [
    objects,
    searchTerm,
    selectedTypes,
    selectedOrgs,
    showActive,
    showArchived,
    contourIdSet,
  ]);

  const summaryStatuses = useMemo(() => {
    if (showActive && showArchived) return undefined;
    if (!showActive && !showArchived) return undefined;
    if (showActive) return "active,pending";
    return "archived";
  }, [showActive, showArchived]);

  const geographyIdsKey = useMemo(
    () => territories.map((item) => item.id).join(","),
    [territories],
  );
  const journalUsesTerritory = journalSync && geographyIdsKey.length > 0;

  const summaryQuery = useQuery({
    queryKey: [
      "objects",
      "summary",
      debouncedSearchTerm,
      selectedTypes.slice().sort().join(","),
      selectedOrgs.slice().sort().join(","),
      summaryStatuses ?? "",
      journalUsesTerritory ? geographyIdsKey : "",
      journalUsesTerritory ? combine : "",
    ],
    queryFn: () =>
      objectsApi.summary({
        q: debouncedSearchTerm || undefined,
        kls_ids: selectedTypes.length ? selectedTypes.join(",") : undefined,
        organization_ids: selectedOrgs.length ? selectedOrgs.join(",") : undefined,
        statuses: summaryStatuses,
        ...(journalUsesTerritory
          ? { geography_ids: geographyIdsKey, geography_combine: combine }
          : {}),
      }),
    placeholderData: (prev) => prev,
  });

  const statsQuery = useQuery({
    queryKey: ["stats"],
    queryFn: () => statsApi.getSystemStats(),
    staleTime: 60_000,
  });

  const filteredObjectCount = summaryQuery.data?.object_count;
  const filteredGeometryCount = summaryQuery.data?.geometry_count;
  const availableObjectCount = statsQuery.data?.totalObjects;
  const availableGeometryCount = statsQuery.data?.totalGeometries;
  const showAvailableRow =
    listFiltersActive ||
    debouncedSearchTerm.length > 0 ||
    (typeof filteredObjectCount === "number" &&
      typeof availableObjectCount === "number" &&
      filteredObjectCount !== availableObjectCount);

  const formatCount = (n: number | undefined, loading: boolean) => {
    if (loading && n == null) return "…";
    if (n == null) return "—";
    return n.toLocaleString("ru-RU");
  };

  // Оптимизация 8.1: считаем только для видимого батча, без полного прохода по всем объектам
  const visibleObjectsBatch = useMemo(() => filteredObjects.slice(0, 30), [filteredObjects]);
  const visibleObjectIdSet = useMemo(() => new Set(visibleObjectsBatch.map((o) => o.id)), [visibleObjectsBatch]);

  useEffect(() => {
    if (visibleObjectsBatch.length === 0) return;
    let cancelled = false;

    const readFromCache = (objectId: number) => {
      const geometries = queryClient.getQueryData(["objects", objectId, "geometries"]);
      const files = queryClient.getQueryData(["objects", objectId, "files"]);
      return {
        geometriesCount: Array.isArray(geometries) ? geometries.length : undefined,
        filesCount: Array.isArray(files) ? files.length : undefined,
      };
    };

    const loadCounts = async () => {
      const initialGeo: Record<number, number> = {};
      const initialFiles: Record<number, number> = {};
      const missingIds: number[] = [];

      for (const obj of visibleObjectsBatch) {
        const cached = readFromCache(obj.id);
        if (cached.geometriesCount !== undefined && cached.filesCount !== undefined) {
          initialGeo[obj.id] = cached.geometriesCount;
          initialFiles[obj.id] = cached.filesCount;
        } else {
          missingIds.push(obj.id);
        }
      }

      if (!cancelled && (Object.keys(initialGeo).length || Object.keys(initialFiles).length)) {
        setObjectGeometries((prev) => ({ ...prev, ...initialGeo }));
        setObjectFiles((prev) => ({ ...prev, ...initialFiles }));
      }

      const chunkSize = 6;
      for (let i = 0; i < missingIds.length; i += chunkSize) {
        const chunk = missingIds.slice(i, i + chunkSize);
        const chunkGeo: Record<number, number> = {};
        const chunkFiles: Record<number, number> = {};

        await Promise.all(
          chunk.map(async (id) => {
            try {
              const [geometries, files] = await Promise.all([
                objectsApi.getGeometries(id),
                objectsApi.getFiles(id),
              ]);
              queryClient.setQueryData(["objects", id, "geometries"], geometries);
              queryClient.setQueryData(["objects", id, "files"], files);
              chunkGeo[id] = Array.isArray(geometries) ? geometries.length : 0;
              chunkFiles[id] = Array.isArray(files) ? files.length : 0;
            } catch {
              chunkGeo[id] = 0;
              chunkFiles[id] = 0;
            }
          })
        );

        if (cancelled) return;
        setObjectGeometries((prev) => ({ ...prev, ...chunkGeo }));
        setObjectFiles((prev) => ({ ...prev, ...chunkFiles }));
      }
    };

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [visibleObjectsBatch, queryClient]);

  // Лёгкая подписка: реагируем только на изменения по видимым объектам, без пересчёта всего списка
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const queryKey = event?.query?.queryKey;
      if (!Array.isArray(queryKey) || queryKey.length !== 3) return;
      const [_, objectId, entity] = queryKey;
      if (typeof objectId !== "number" || !visibleObjectIdSet.has(objectId)) return;

      // Кэш уведомляет подписчиков синхронно; setState здесь может сработать во время рендера
      // родителя (например Home), если тот обновил тот же ключ запроса — React запрещает это.
      if (entity === "geometries") {
        const geometries = queryClient.getQueryData(["objects", objectId, "geometries"]);
        const n = Array.isArray(geometries) ? geometries.length : 0;
        queueMicrotask(() => {
          setObjectGeometries((prev) => ({ ...prev, [objectId]: n }));
        });
      } else if (entity === "files") {
        const files = queryClient.getQueryData(["objects", objectId, "files"]);
        const n = Array.isArray(files) ? files.length : 0;
        queueMicrotask(() => {
          setObjectFiles((prev) => ({ ...prev, [objectId]: n }));
        });
      }
    });

    return () => unsubscribe();
  }, [queryClient, visibleObjectIdSet]);

  const [objectDetailTab, setObjectDetailTab] = useState("description");
  /** Вкладка «Организации» переименована в «Связи» (value links). */
  useEffect(() => {
    setObjectDetailTab((prev) => (prev === "organizations" ? "links" : prev));
  }, []);

  const [sidebarWidthPx, setSidebarWidthPx] = useState(() => {
    if (typeof window === "undefined") return SIDEBAR_WIDTH_DEFAULT;
    try {
      const raw = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
      if (raw) {
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n)) {
          return clampSidebarWidthPx(n);
        }
      }
    } catch {
      /* ignore */
    }
    return clampSidebarWidthPx(SIDEBAR_WIDTH_DEFAULT);
  });

  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const onResize = () => {
      setSidebarWidthPx((w) => clampSidebarWidthPx(w));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidthPx));
    } catch {
      /* ignore */
    }
  }, [sidebarWidthPx]);

  useEffect(() => {
    if (!isResizing) return;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = prev;
    };
  }, [isResizing]);

  const canAccessAI = hasPermission("admin.access");
  const { selectedGeometry, toggleSidebar } = useMapUi();
  const isMobile = useIsMobile();

  const closeSidebarOnMobile = useCallback(() => {
    if (isMobile && !isCollapsed) toggleSidebar();
  }, [isMobile, isCollapsed, toggleSidebar]);

  const searchResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) return [];
    return filteredObjects.slice(0, 6);
  }, [searchTerm, filteredObjects]);

  const showDropdown = isSearchFocused && searchTerm.trim().length >= 2 && searchResults.length > 0;

  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  const handleSearchResultClick = useCallback((obj: GeoObject) => {
    const sameSelected =
      selectedObject != null && Number(selectedObject.id) === Number(obj.id);

    if (sameSelected) {
      clearSelectedObject();
      setIsSearchFocused(false);
      setSearchTerm("");
      return;
    }

    selectObject(obj, { openSidebarDetails: false });
    setIsSearchFocused(false);
    setSearchTerm("");
  }, [selectObject, clearSelectedObject, selectedObject?.id]);

  const handleListObjectClick = useCallback((obj: GeoObject) => {
    const sameSelected =
      selectedObject != null && Number(selectedObject.id) === Number(obj.id);

    if (sameSelected) {
      clearSelectedObject();
      return;
    }

    selectObject(obj, { openSidebarDetails: false });
  }, [selectObject, clearSelectedObject, selectedObject?.id]);

  const handleOpenObjectDetails = useCallback((obj: GeoObject) => {
    selectObject(obj, { openSidebarDetails: true });
  }, [selectObject]);

  const startSidebarResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (isMobile || isCollapsed) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      const el = e.currentTarget;
      const startX = e.clientX;
      const startW = sidebarWidthPx;
      setIsResizing(true);
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        setSidebarWidthPx(clampSidebarWidthPx(startW + delta));
      };
      const onUp = (ev: PointerEvent) => {
        try {
          if (el.hasPointerCapture(ev.pointerId)) {
            el.releasePointerCapture(ev.pointerId);
          }
        } catch {
          /* ignore */
        }
        setIsResizing(false);
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.removeEventListener("pointercancel", onUp);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
    },
    [isMobile, isCollapsed, sidebarWidthPx],
  );

  const mobileSidebarClasses = `fixed inset-y-0 left-0 z-40 flex w-[85vw] max-w-96 select-none flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-lg transition-transform duration-300 [&_input]:select-text [&_textarea]:select-text ${
    isCollapsed ? "-translate-x-full" : "translate-x-0"
  }`;

  const desktopAsideClassName = cn(
    "relative z-20 flex h-full min-h-0 min-w-0 flex-shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-md select-none [&_input]:select-text [&_textarea]:select-text",
    !isResizing && "transition-[width] duration-200 ease-out",
  );

  return (
    <>
      {isMobile && !isCollapsed && (
        <div
          className="fixed inset-0 bg-black/30 z-30"
          onClick={closeSidebarOnMobile}
        />
      )}
      <aside
        className={isMobile ? mobileSidebarClasses : desktopAsideClassName}
        style={
          isMobile
            ? undefined
            : { width: isCollapsed ? 0 : sidebarWidthPx }
        }
      >
      {!isMobile && !isCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину панели"
          className="absolute right-0 top-0 bottom-0 z-30 w-3 -mr-1.5 cursor-col-resize touch-none select-none flex justify-center group"
          onPointerDown={startSidebarResize}
        >
          <span className="w-px h-full bg-border group-hover:bg-primary/50 group-active:bg-primary transition-colors" />
        </div>
      )}
      {!isCollapsed && (
        <Tabs
          value={sidebarTab}
          onValueChange={(v) => {
            const tab = v as SidebarTab;
            if (requestSidebarTabChange) requestSidebarTabChange(tab);
            else setSidebarTab(tab);
          }}
          className="flex flex-col h-full"
        >
          <div className="flex-shrink-0 border-b border-sidebar-border px-1 pt-1">
            <TabsList className={`w-full grid ${canAccessAI ? "grid-cols-4" : "grid-cols-3"} h-9`}>
              <TabsTrigger value="objects" className="text-xs gap-1 px-1">
                <List className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">Объекты</span>
              </TabsTrigger>
              <TabsTrigger
                value="details"
                className="text-xs gap-1 px-1"
                disabled={!selectedObject}
              >
                <Info className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">Детали</span>
              </TabsTrigger>
              <TabsTrigger value="layers" className="text-xs gap-1 px-1">
                <Layers className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">Слои</span>
              </TabsTrigger>
              {canAccessAI && (
                <TabsTrigger value="assistant" className="text-xs gap-1 px-1">
                  <BrainCircuit className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline">ИИ</span>
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* === Вкладка: Объекты (текущий контент сайдбара) === */}
          <TabsContent value="objects" className="flex-1 min-h-0 flex flex-col mt-0 data-[state=inactive]:hidden">
            <EntityListPaneHeader title="Объекты инфраструктуры" className="flex-shrink-0" hideTitle>
              <div className="flex justify-between items-center gap-2">
                <ModuleRegistryTitle
                  title="Объекты инфраструктуры"
                  helpLabel="Справка по реестру объектов"
                  withHeading
                >
                  <p className="font-medium">Зачем</p>
                  <p>Реестр объектов инфраструктуры: поиск, отбор списка и переход к карточке на карте.</p>
                  <p className="font-medium mt-2">Как работать</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Поиск по названию; фильтр — тип, орган управления, статус, журнал по территории</li>
                    <li>«+» — создать объект; корзина — режим массового удаления</li>
                    <li>Клик по строке выбирает объект; повторный клик снимает выбор</li>
                    <li>Вкладки: Объекты, Детали, Слои, ИИ (если доступен)</li>
                  </ul>
                  <p className="font-medium mt-2">Клавиатура</p>
                  <ul className="list-disc pl-4">
                    <li>Esc на карте / сброс выбора — снять выделение объекта</li>
                  </ul>
                </ModuleRegistryTitle>
                <div className="flex shrink-0 gap-1">
                  {!isSelectionMode ? (
                    <>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="relative h-7 w-7"
                            title="Фильтры списка"
                            aria-label="Фильтры списка"
                          >
                            <ListFilter className="h-3.5 w-3.5" />
                            {listFiltersActive ? (
                              <span
                                className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
                                aria-hidden
                              />
                            ) : null}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="end"
                          className="z-[230] w-[min(100vw-1.5rem,24rem)] space-y-3 p-3"
                        >
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-foreground">Фильтры списка</p>
                            <SectionHelpTooltip label="Справка: фильтры объектов" side="bottom">
                              <p className="font-medium">Зачем</p>
                              <p>
                                Отобрать реестр объектов, не занимая место под поиском развёрнутой формой.
                              </p>
                              <p className="font-medium mt-2">Как работать</p>
                              <ul className="list-disc pl-4 space-y-1">
                                <li>Тип, орган управления и статус действуют на список слева</li>
                                <li>
                                  «Журнал по территории» — только объекты в выбранных на карте границах
                                </li>
                                <li>«+» — создать объект; корзина — массовое удаление</li>
                              </ul>
                            </SectionHelpTooltip>
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            Фильтры относятся к реестру слева, не к подложке карты (MVT — во вкладке
                            «Слои»).
                          </p>
                          <div className="space-y-1">
                            <Label className="text-xs">Тип объекта</Label>
                            <MultiSelectCombobox
                              options={objectTypeOptions}
                              values={selectedTypes}
                              onValuesChange={setSelectedTypes}
                              placeholder="Все типы"
                              searchPlaceholder="Поиск типа..."
                              emptyText="Типы не найдены"
                              allItemsLabel="Все типы"
                              maxDisplayedItems={1}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Орган управления</Label>
                            <MultiSelectCombobox
                              options={organizationOptions}
                              values={selectedOrgs}
                              onValuesChange={setSelectedOrgs}
                              placeholder="Все органы управления"
                              searchPlaceholder="Поиск органа управления..."
                              emptyText="Органы управления не найдены"
                              allItemsLabel="Все органы управления"
                              maxDisplayedItems={1}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Статус</Label>
                            <div className="flex space-x-4">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="active"
                                  checked={showActive}
                                  onCheckedChange={(checked: boolean) => setShowActive(checked)}
                                />
                                <label
                                  htmlFor="active"
                                  className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  Активные
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="archived"
                                  checked={showArchived}
                                  onCheckedChange={(checked: boolean) => setShowArchived(checked)}
                                />
                                <label
                                  htmlFor="archived"
                                  className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  Архивные
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5">
                            <div className="flex min-w-0 items-center gap-1">
                              <Label htmlFor="journal-geography-sync" className="text-xs">
                                Журнал по территории
                              </Label>
                              <SectionHelpTooltip label="Справка: журнал и территория">
                                <p>
                                  Пока выключено, список показывает объекты как раньше, независимо от
                                  фильтра на карте.
                                </p>
                                <p className="mt-1">
                                  Включите, чтобы в журнале остались только объекты, чьи контуры
                                  попадают в выбранные границы (объединение или пересечение — как на
                                  карте).
                                </p>
                              </SectionHelpTooltip>
                            </div>
                            <Switch
                              id="journal-geography-sync"
                              checked={journalSync}
                              onCheckedChange={setJournalSync}
                              disabled={territories.length === 0}
                              aria-label="Фильтровать журнал по территории на карте"
                            />
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={toggleSelectionMode}
                        className="h-7 w-7"
                        title="Массовое удаление"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <PermissionGuard permission="objects.create">
                        <Button 
                          size="icon" 
                          onClick={onCreateObject} 
                          className="h-7 w-7"
                          title="Добавить объект"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </PermissionGuard>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={toggleSelectionMode}
                        className="h-7 text-xs"
                      >
                        Отменить
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setBulkNewStatus("archived"); setShowStatusDialog(true); }}
                        disabled={selectedObjects.size === 0 || updateStatusMutation.isPending}
                        className="h-7 text-xs"
                        title="Изменить статус выбранных"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Статус
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowBulkFieldsDialog(true)}
                        disabled={selectedObjects.size === 0 || updateBulkFieldsMutation.isPending}
                        className="h-7 text-xs"
                        title="Массовое изменение полей"
                      >
                        Поля
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleDeleteSelected}
                        disabled={selectedObjects.size === 0 || deleteObjectsMutation.isPending}
                        className="h-7 text-xs"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        ({selectedObjects.size})
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {isSelectionMode && (
                <div className="flex justify-between items-center mt-2 text-sm text-muted-foreground">
                  <span>Выбрано: {selectedObjects.size}</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={selectAllObjects}
                      className="h-6 text-xs"
                    >
                      Выбрать все
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearSelection}
                      className="h-6 text-xs"
                    >
                      Снять все
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="relative mt-3" ref={searchWrapperRef}>
                <EntitySearchInput
                  placeholder="Поиск по названию, адресу..."
                  showIcon
                  value={searchTerm}
                  onChange={setSearchTerm}
                  onFocus={() => setIsSearchFocused(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setIsSearchFocused(false);
                  }}
                />
                {contourIdSet ? (
                  <div className="mt-2 flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2 py-1.5 text-xs">
                    <ScanSearch className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-foreground">
                      Контур: {contourIdSet.size} объект(ов)
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => onClearContourFilter?.()}
                      aria-label="Сбросить фильтр по контуру"
                      title="Сбросить фильтр по контуру"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : null}
                {showDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-popover text-popover-foreground border border-border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto">
                    {searchResults.map((obj) => (
                      <button
                        key={obj.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground border-b border-border/60 last:border-b-0"
                        onClick={() => handleSearchResultClick(obj)}
                      >
                        <div className="text-sm font-medium truncate">{obj.name}</div>
                        {obj.address && (
                          <div className="text-xs text-muted-foreground truncate">{obj.address}</div>
                        )}
                      </button>
                    ))}
                    {filteredObjects.length > 6 && (
                      <div className="px-3 py-1.5 text-xs text-muted-foreground text-center">
                        Ещё {filteredObjects.length - 6} совпадений — уточните запрос
                      </div>
                    )}
                  </div>
                )}
              </div>
            </EntityListPaneHeader>
            
            <div className="overflow-y-auto flex-grow min-h-0 scrollbar-thin">
              {isLoading ? (
                <EntityListLoadingState rows={5} rowClassName="h-20 w-full" />
              ) : filteredObjects.length === 0 ? (
                <EntityListEmptyState text="Нет объектов, соответствующих фильтрам" />
              ) : (
                filteredObjects.map((object) => (
                  <EntityListItemShell
                    key={object.id}
                    selected={selectedObject != null && Number(selectedObject.id) === Number(object.id)}
                    selectedForBulk={selectedObjects.has(object.id)}
                    onClick={() => (isSelectionMode ? undefined : handleListObjectClick(object))}
                    onDoubleClick={() => (isSelectionMode ? undefined : handleOpenObjectDetails(object))}
                  >
                      <div className="flex items-start gap-3">
                        {isSelectionMode && (
                          <Checkbox
                            checked={selectedObjects.has(object.id)}
                            onCheckedChange={() => toggleObjectSelection(object.id, { stopPropagation: () => {} } as React.MouseEvent)}
                            className="mt-1"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="flex-1 min-w-0 relative pr-9">
                          <div className="flex justify-between items-start gap-1">
                            <h3 className="font-medium text-sm truncate">{object.name}</h3>
                            <span 
                              className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${
                                object.status === "active" 
                                  ? "bg-green-100 text-green-800 dark:bg-green-950/70 dark:text-green-300"
                                  : object.status === "pending"
                                  ? "bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {object.status === "active" ? "Активный" : 
                               object.status === "pending" ? "На рассмотрении" : "Архивный"}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 truncate" title={objectTypes.find(t => t.id === object.kls_id)?.name}>
                            Тип: {(() => {
                              const type = objectTypes.find(t => t.id === object.kls_id);
                              return type?.name_short || type?.name || "Не указан";
                            })()}
                          </div>
                          <div className="text-xs text-muted-foreground/80 mt-0.5 truncate" title={organizations.find(o => o.id === object.organization_id)?.name}>
                            {(() => {
                              const org = organizations.find(o => o.id === object.organization_id);
                              return org?.name_short || org?.name || "Орган управления не указан";
                            })()}
                          </div>
                          <div className="flex items-center text-xs text-muted-foreground mt-1.5">
                            <MapPin className="h-3 w-3 mr-1 text-primary" />
                            <span>{objectGeometries[object.id] || 0}</span>
                            <FileText className="h-3 w-3 ml-3 mr-1 text-amber-500" />
                            <span>{objectFiles[object.id] || 0}</span>
                          </div>
                          {!isSelectionMode && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="absolute right-0 bottom-0 h-6 w-6 text-muted-foreground hover:text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenObjectDetails(object);
                              }}
                              title="Открыть детали объекта"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                  </EntityListItemShell>
                ))
              )}
            </div>

            <div className="shrink-0 border-t border-sidebar-border bg-sidebar/95 px-3 py-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs tabular-nums">
                  <span className="inline-flex items-center gap-1 text-foreground">
                    <List className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="font-semibold">
                      {formatCount(filteredObjectCount, summaryQuery.isFetching)}
                    </span>
                    <span className="text-muted-foreground">объект.</span>
                  </span>
                  <span className="text-muted-foreground/50" aria-hidden>
                    ·
                  </span>
                  <span className="inline-flex items-center gap-1 text-foreground">
                    <MapPin className="h-3 w-3 shrink-0 text-primary" aria-hidden />
                    <span className="font-semibold">
                      {formatCount(filteredGeometryCount, summaryQuery.isFetching)}
                    </span>
                    <span className="text-muted-foreground">геом.</span>
                  </span>
                </div>
                <SectionHelpTooltip label="Справка: итоги реестра" side="top">
                  <p className="font-medium">Зачем</p>
                  <p>
                    Полный срез объектов и геометрий по текущим фильтрам — не только строки, уже
                    загруженные в список.
                  </p>
                  <p className="font-medium mt-2">Как читать</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Цифры в строке — COUNT на сервере (поиск, тип, орган, статус, журнал по территории)</li>
                    <li>«Доступно» — весь ваш RBAC-срез без UI-фильтров</li>
                  </ul>
                </SectionHelpTooltip>
              </div>
              {showAvailableRow ? (
                <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground tabular-nums">
                  Доступно: {formatCount(availableObjectCount, statsQuery.isLoading)} объ. ·{" "}
                  {formatCount(availableGeometryCount, statsQuery.isLoading)} геом.
                </p>
              ) : null}
            </div>
          </TabsContent>

          {/* === Вкладка: Детали объекта === */}
          <TabsContent value="details" className="mt-0 flex min-h-0 flex-1 select-text flex-col data-[state=inactive]:hidden">
            {selectedObject == null ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground text-sm">
                Выберите объект из списка для просмотра деталей
              </div>
            ) : sidebarTab === "details" ? (
              <>
                <div className="px-3 py-2 border-b border-sidebar-border flex items-center justify-between bg-sidebar/95">
                  <div className="text-xs text-muted-foreground truncate">
                    {selectedObject.name}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs shrink-0"
                    onClick={() => {
                      if (requestSidebarTabChange) requestSidebarTabChange("objects");
                      else setSidebarTab("objects");
                    }}
                    title="Вернуться к списку объектов"
                  >
                    К списку
                  </Button>
                </div>
                <ObjectPanel
                  variant="embedded"
                  objectId={selectedObject.id}
                  geometries={selectedObjectGeometries}
                  files={selectedObjectFiles}
                  focusedGeometryId={focusedGeometryId}
                  activeTab={objectDetailTab}
                  onTabChange={setObjectDetailTab}
                  onClose={clearSelectedObject}
                  isLoading={geometriesLoading}
                  onCardDirtyChange={onCardDirtyChange}
                />
              </>
            ) : null}
          </TabsContent>

          {/* === Вкладка: Слои === */}
          <TabsContent value="layers" className="flex-1 min-h-0 overflow-y-auto mt-0 data-[state=inactive]:hidden">
            <LayerPanel />
          </TabsContent>

          {/* === Вкладка: AI-ассистент === */}
          {canAccessAI && (
            <TabsContent value="assistant" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
              <AIChatPanel selectedGeometry={selectedGeometry} />
            </TabsContent>
          )}
        </Tabs>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Подтверждение удаления</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить выбранные объекты ({selectedObjects.size})? 
              Это действие нельзя отменить. Все связанные геометрии и файлы также будут удалены.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteObjectsMutation.isPending}
            >
              {deleteObjectsMutation.isPending ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Изменение статуса</AlertDialogTitle>
            <AlertDialogDescription>
              Изменить статус для {selectedObjects.size} объектов:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 py-2">
            <Button
              size="sm"
              variant={bulkNewStatus === "active" ? "default" : "outline"}
              onClick={() => setBulkNewStatus("active")}
              className="flex-1"
            >
              Активный
            </Button>
            <Button
              size="sm"
              variant={bulkNewStatus === "archived" ? "default" : "outline"}
              onClick={() => setBulkNewStatus("archived")}
              className="flex-1"
            >
              Архивный
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                updateStatusMutation.mutate({ ids: Array.from(selectedObjects), status: bulkNewStatus });
                setShowStatusDialog(false);
              }}
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? "Обновление..." : "Применить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showBulkFieldsDialog}
        onOpenChange={(open) => {
          setShowBulkFieldsDialog(open);
          if (!open) {
            setBulkTypeId("");
            setBulkOrganizationId("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Массовое изменение полей</AlertDialogTitle>
            <AlertDialogDescription>
              Выбрано объектов: {selectedObjects.size}. Укажите новые значения для типа и/или организации.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="block text-xs text-muted-foreground mb-1">Новый тип объекта</Label>
              <Combobox
                options={bulkTypeOptions}
                value={bulkTypeId || undefined}
                onValueChange={(v) => setBulkTypeId(v || "")}
                placeholder="Оставить без изменений"
                searchPlaceholder="Поиск типа..."
                emptyText="Типы не найдены"
                allowClear
              />
            </div>
            <div>
              <Label className="block text-xs text-muted-foreground mb-1">Новая организация</Label>
              <Combobox
                options={bulkOrganizationOptions}
                value={bulkOrganizationId || undefined}
                onValueChange={(v) => setBulkOrganizationId(v || "")}
                placeholder="Оставить без изменений"
                searchPlaceholder="Поиск организации..."
                emptyText="Организации не найдены"
                allowClear
              />
            </div>
            {!bulkTypeId && !bulkOrganizationId && (
              <div className="text-xs text-amber-600">
                Выберите хотя бы одно поле для изменения.
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!bulkTypeId && !bulkOrganizationId) return;
                updateBulkFieldsMutation.mutate({
                  ids: Array.from(selectedObjects),
                  klsId: bulkTypeId ? Number(bulkTypeId) : undefined,
                  organizationId: bulkOrganizationId ? Number(bulkOrganizationId) : undefined,
                });
                setShowBulkFieldsDialog(false);
              }}
              disabled={!bulkTypeId && !bulkOrganizationId || updateBulkFieldsMutation.isPending}
            >
              {updateBulkFieldsMutation.isPending ? "Обновление..." : "Применить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
    </>
  );
}
